import { CircleAlert, Info, ShieldCheck, TriangleAlert } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';

import { cx, variantClass } from './styles';

const alertTones = {
  info: {
    icon: Info,
    style: 'bg-info/10 text-info-text'
  },
  success: {
    icon: ShieldCheck,
    style: 'bg-success/10 text-success-text'
  },
  warning: {
    icon: TriangleAlert,
    style: 'bg-warning/10 text-warning-text'
  },
  danger: {
    icon: CircleAlert,
    style: 'bg-danger/10 text-danger-text'
  }
} as const;

export type AlertTone = keyof typeof alertTones;

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  actions?: ReactNode;
  description?: ReactNode;
  title: ReactNode;
  tone?: AlertTone;
}

export function Alert({
  actions,
  className,
  description,
  title,
  tone = 'info',
  ...props
}: AlertProps) {
  const Icon = alertTones[tone].icon;

  return (
    <div
      {...props}
      className={cx(
        'flex gap-3 rounded-lg p-4',
        description ? 'items-start' : 'items-center',
        variantClass(
          {
            danger: alertTones.danger.style,
            info: alertTones.info.style,
            success: alertTones.success.style,
            warning: alertTones.warning.style
          },
          tone
        ),
        className
      )}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <Icon aria-hidden className={cx('size-4 shrink-0', description ? 'mt-1' : null)} />
      <div
        className={cx('grid min-w-0 flex-1 gap-1', description ? null : 'kitsune-optical-center')}
      >
        <strong className="text-sm font-semibold">{title}</strong>
        {description ? <div className="text-sm text-text-muted">{description}</div> : null}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </div>
  );
}
